/**
 * SSO Callback Page for Kit19 Sales CRM
 * 
 * Handles the SSO callback after user authenticates
 * Processes authentication response and redirects to dashboard
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ssoConfig from '../config/ssoConfig';
import { Kit19SSOClient } from '../lib/sso/ssoClient';

/**
 * SSOCallback Component
 * 
 * Validates JWT token and redirects user with complete user setup
 */
const SSOCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);

  // Create SSO client instance
  const ssoClient = new Kit19SSOClient(ssoConfig);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get parameters from URL
        const code = searchParams.get('code');
        const token = searchParams.get('token');
        const state = searchParams.get('state');
        const deviceId = searchParams.get('deviceId');
        let returnUrl = searchParams.get('returnUrl') || '/';
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Decode returnUrl once (single encoding)
        try {
          returnUrl = decodeURIComponent(returnUrl);
        } catch (e) {
          // fallback: use as-is
        }
        // Store returnUrl in sessionStorage for later use
        if (returnUrl) {
          sessionStorage.setItem('sso_return_url', returnUrl);
        }

        // Handle error response
        if (error) {
          console.error('SSO Error Response:', error, errorDescription);
          setError(`${error}: ${errorDescription || 'Unknown error'}`);
          setIsProcessing(false);

          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        // Handle direct token from external application (for cross-app SSO)
        if (token && !code) {
          try {
            // Validate token format (must be JWT with 3 parts)
            const tokenParts = token.split('.');
            if (tokenParts.length !== 3) {
              throw new Error('Invalid token format - must be JWT');
            }

            // Validate token using ssoClient
            const validation = await ssoClient.validateToken(token);

            if (!validation.valid) {
              throw new Error(validation.message || 'Token validation failed');
            }

            // Store session data using ssoClient
            ssoClient.storeSession(token, validation.userData);

            // Also store token as 'Token' for legacy compatibility in Kit19Sales
            localStorage.setItem('Token', token);
            // Map SSO userId to legacy 'userId' key
            if (validation.userData.userId || validation.userData.User_ID) {
              localStorage.setItem('userId', validation.userData.userId || validation.userData.User_ID);
            }
            if (validation.userData.parentId || validation.userData.ParentID) {
              localStorage.setItem('ParentID', validation.userData.parentId || validation.userData.ParentID);
            }

            // Map the rest of the legacy display fields so the Header updates correctly
            localStorage.setItem('LoginName', validation.userData.LoginName || '');
            localStorage.setItem('kit_loginName', validation.userData.LoginName || '');
            localStorage.setItem('FName', validation.userData.FName || '');
            localStorage.setItem('LName', validation.userData.LName || '');
            localStorage.setItem('DisplayName', validation.userData.DisplayName || '');
            localStorage.setItem('email', validation.userData.EMail || '');
            localStorage.setItem('Mobile', validation.userData.Mobile || '');
            localStorage.setItem('ProfilePicturePath', validation.userData.ProfilePicturePath || '');

            if (deviceId) {
              localStorage.setItem('kit19_device_id', deviceId);
            }

            localStorage.setItem('kit_auth_type', 'sso'); // Track as SSO login

            // Clean up URL params
            window.history.replaceState({}, document.title, window.location.pathname);

            // Redirect to return URL or default
            const redirectUrl = sessionStorage.getItem('sso_return_url') || '/';
            sessionStorage.removeItem('sso_return_url');

            setTimeout(() => {
              navigate(redirectUrl, { replace: true });
            }, 100);
            return;
          } catch (err) {
            console.error('SSOCallback: Token validation error:', err);
            setError(err.message || 'Invalid token');
            setIsProcessing(false);

            setTimeout(() => {
              navigate('/login', { replace: true });
            }, 3000);
            return;
          }
        }

        // Handle authorization code
        if (code) {
          try {
            // Exchange code for token
            const response = await fetch(
              `${ssoConfig.ssoApiUrl}${ssoConfig.endpoints.authenticate}`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  code,
                  state,
                  redirectUri: ssoConfig.callbackUrl,
                  clientId: ssoConfig.appId
                })
              }
            );

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.message || `Authentication failed with status ${response.status}`);
            }

            const data = await response.json();

            // Store token and user info
            if (data.accessToken || data.token) {
              const accessToken = data.accessToken || data.token;
              localStorage.setItem(ssoConfig.tokenStorageKey, accessToken);
              localStorage.setItem('Token', accessToken); // Legacy compatibility

              if (data.refreshToken) {
                localStorage.setItem(ssoConfig.refreshTokenKey, data.refreshToken);
              }

              if (data.user) {
                const userInfo = data.user;
                localStorage.setItem(ssoConfig.userStorageKey, JSON.stringify(userInfo));

                // Store user ID with legacy keys too
                if (userInfo.userId) {
                  localStorage.setItem('kit_userId', userInfo.userId);
                  localStorage.setItem('userId', userInfo.userId);
                }
                
                // Overwrite display fields to prevent stale data
                localStorage.setItem('LoginName', userInfo.userLogin || userInfo.username || '');
                localStorage.setItem('kit_loginName', userInfo.userLogin || userInfo.username || '');
                localStorage.setItem('FName', userInfo.firstName || userInfo.fName || '');
                localStorage.setItem('LName', userInfo.lastName || userInfo.lName || '');
                localStorage.setItem('DisplayName', userInfo.displayName || '');
                localStorage.setItem('email', userInfo.email || '');
                localStorage.setItem('Mobile', userInfo.mobile || '');
                localStorage.setItem('ProfilePicturePath', userInfo.profilePicture || userInfo.profileImage || '');
              }

              localStorage.setItem('kit_auth_type', 'sso'); // Track as SSO login

              // Get return URL from session or use default
              const returnUrl = sessionStorage.getItem('sso_return_url') || ssoConfig.redirectOnLogin;
              sessionStorage.removeItem('sso_return_url');

              // Redirect to dashboard or return URL
              setTimeout(() => {
                navigate(returnUrl, { replace: true });
              }, 100);
            } else {
              throw new Error('No access token received from SSO server');
            }
          } catch (err) {
            console.error('SSOCallback: Token exchange error:', err);
            setError(err.message || 'Failed to complete authentication');
            setIsProcessing(false);

            // Redirect to login after 3 seconds
            setTimeout(() => {
              navigate('/login', { replace: true });
            }, 3000);
          }
        } else {
          setError('No authorization code received');
          setIsProcessing(false);

          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
        }
      } catch (err) {
        console.error('SSOCallback: Unexpected error:', err);
        setError(err.message || 'An error occurred during authentication');
        setIsProcessing(false);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (isProcessing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <div className="spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #2a9629',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
          Completing login... Please wait
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '4px', maxWidth: '400px' }}>
          <h4 style={{ color: '#d32f2f' }}>Authentication Error</h4>
          <p>{error}</p>
          <small style={{ color: '#999' }}>Redirecting to login...</small>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div className="spinner" style={{
        width: '40px',
        height: '40px',
        border: '4px solid #e5e7eb',
        borderTop: '4px solid #2a9629',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SSOCallback;
